import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SearchFilters } from './types';
import './FilterPanel.scss';

interface FilterPanelProps {
  filters: SearchFilters;
  onFiltersChange: (filters: Partial<SearchFilters>) => void;
  showFilters: boolean;
  onClose: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  showFilters,
  onClose
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<SearchFilters>(filters);

  // Update local filters when props change
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleListingTypeChange = useCallback((type: 'sell' | 'buy' | 'both') => {
    setLocalFilters(prev => ({ ...prev, listingType: type }));
  }, []);

  const handleSizeChange = useCallback((field: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : parseInt(value);
    setLocalFilters(prev => ({
      ...prev,
      size: { ...prev.size, [field]: numValue }
    }));
  }, []);

  const handleRoomsChange = useCallback((rooms: string | null) => {
    setLocalFilters(prev => ({ ...prev, rooms }));
  }, []);

  const handleFloorChange = useCallback((field: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : parseInt(value);
    setLocalFilters(prev => ({
      ...prev,
      floor: { ...prev.floor, [field]: numValue }
    }));
  }, []);

  const handleRenovationStatusChange = useCallback((status: string | null) => {
    setLocalFilters(prev => ({ ...prev, renovationStatus: status }));
  }, []);

  const handleAmenityChange = useCallback((amenity: 'hasElevator' | 'hasBalcony', value: boolean) => {
    setLocalFilters(prev => ({ ...prev, [amenity]: value }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    onFiltersChange(localFilters);
    onClose();
  }, [localFilters, onFiltersChange, onClose]);

  const handleDismiss = useCallback(() => {
    setLocalFilters(filters);
    onClose();
  }, [filters, onClose]);

  const handleClearAll = useCallback(() => {
    setLocalFilters(prev => ({
      ...prev,
      includeIncompleteDetails: true,
      listingType: 'both',
      size: { min: null, max: null },
      rooms: null,
      floor: { min: null, max: null },
      renovationStatus: null,
      hasElevator: null,
      hasBalcony: null,
    }));
  }, []);

  const hasAnyLocalFilter =
    !localFilters.includeIncompleteDetails ||
    localFilters.listingType !== 'both' ||
    localFilters.size.min !== null || localFilters.size.max !== null ||
    localFilters.rooms !== null ||
    localFilters.floor.min !== null || localFilters.floor.max !== null ||
    localFilters.renovationStatus !== null ||
    localFilters.hasElevator === true ||
    localFilters.hasBalcony === true;

  if (!showFilters) return null;

  return createPortal(
    <div className="filter-panel-overlay" onClick={handleDismiss}>
      <div className="filter-panel" onClick={(e) => e.stopPropagation()}>
        <div className="filter-panel__header">
          <h2 className="filter-panel__title">{t('exploreListings.filters')}</h2>
          {hasAnyLocalFilter && (
            <button
              type="button"
              className="filter-panel__clear-button"
              onClick={handleClearAll}
            >
              {t('exploreListings.clearAll')}
            </button>
          )}
        </div>

        <div className="filter-panel__content">
          <div className="filter-panel__section filter-panel__section--incomplete">
            <label className="filter-panel__checkbox-label filter-panel__incomplete-label">
              <input
                type="checkbox"
                className="filter-panel__checkbox"
                checked={localFilters.includeIncompleteDetails}
                onChange={(event) => setLocalFilters((prev) => ({
                  ...prev,
                  includeIncompleteDetails: event.target.checked,
                }))}
              />
              <span>{t('exploreListings.includeIncompleteDetails')}</span>
            </label>
            <p className="filter-panel__incomplete-help">
              {t('exploreListings.includeIncompleteDetailsHelp')}
            </p>
          </div>
          {/* Listing Type */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.listingType')}</h3>
            <div className="filter-panel__button-group filter-panel__button-group--listing-type">
              <button
                className={`filter-panel__toggle-button filter-panel__toggle-button--listing-type ${localFilters.listingType === 'both' ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleListingTypeChange('both')}
              >
                {t('exploreListings.both')}
              </button>
              <button
                className={`filter-panel__toggle-button filter-panel__toggle-button--listing-type ${localFilters.listingType === 'sell' ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleListingTypeChange('sell')}
              >
                {t('exploreListings.forSale')}
              </button>
              <button
                className={`filter-panel__toggle-button filter-panel__toggle-button--listing-type ${localFilters.listingType === 'buy' ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleListingTypeChange('buy')}
              >
                {t('exploreListings.buyingInterests')}
              </button>
            </div>
          </div>

          {/* Property Size */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.propertySize')}</h3>
            <div className="filter-panel__range-inputs">
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.minSize')}
                  value={localFilters.size.min ?? ''}
                  onChange={(e) => handleSizeChange('min', e.target.value)}
                />
                <span className="filter-panel__input-suffix">m²</span>
              </div>
              <span className="filter-panel__range-separator">–</span>
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.maxSize')}
                  value={localFilters.size.max ?? ''}
                  onChange={(e) => handleSizeChange('max', e.target.value)}
                />
                <span className="filter-panel__input-suffix">m²</span>
              </div>
            </div>
          </div>

          {/* Rooms */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.rooms')}</h3>
            <div className="filter-panel__button-group filter-panel__button-group--rooms">
              <button
                className={`filter-panel__toggle-button ${!localFilters.rooms ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleRoomsChange(null)}
              >
                {t('exploreListings.any')}
              </button>
              {['1', '2', '3', '4', '5+'].map(room => (
                <button
                  key={room}
                  className={`filter-panel__toggle-button ${localFilters.rooms === room ? 'filter-panel__toggle-button--active' : ''}`}
                  onClick={() => handleRoomsChange(room)}
                >
                  {room}
                </button>
              ))}
            </div>
          </div>

          {/* Floor */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.floor')}</h3>
            <div className="filter-panel__range-inputs">
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.minFloor')}
                  value={localFilters.floor.min ?? ''}
                  onChange={(e) => handleFloorChange('min', e.target.value)}
                />
              </div>
              <span className="filter-panel__range-separator">–</span>
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.maxFloor')}
                  value={localFilters.floor.max ?? ''}
                  onChange={(e) => handleFloorChange('max', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Renovation Status */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.renovationStatus')}</h3>
            <select
              className="filter-panel__select"
              value={localFilters.renovationStatus || ''}
              onChange={(e) => handleRenovationStatusChange(e.target.value || null)}
            >
              <option value="">{t('exploreListings.anyCondition')}</option>
              <option value="recently_renovated">{t('exploreListings.recentlyRenovated')}</option>
              <option value="good_condition">{t('exploreListings.goodCondition')}</option>
              <option value="needs_renovation">{t('exploreListings.needsRenovation')}</option>
            </select>
          </div>

          {/* Amenities */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.amenities')}</h3>
            <div className="filter-panel__checkbox-group">
              <label className="filter-panel__checkbox-label">
                <input
                  type="checkbox"
                  className="filter-panel__checkbox"
                  checked={localFilters.hasElevator === true}
                  onChange={(e) => handleAmenityChange('hasElevator', e.target.checked ? true : null)}
                />
                <span>{t('exploreListings.hasElevator')}</span>
              </label>
              <label className="filter-panel__checkbox-label">
                <input
                  type="checkbox"
                  className="filter-panel__checkbox"
                  checked={localFilters.hasBalcony === true}
                  onChange={(e) => handleAmenityChange('hasBalcony', e.target.checked ? true : null)}
                />
                <span>{t('exploreListings.hasBalcony')}</span>
              </label>
            </div>
          </div>
        </div>

        <div className="filter-panel__footer">
          <div className="filter-panel__footer-actions">
            <button className="filter-panel__cancel-button" onClick={handleDismiss}>
              {t('exploreListings.cancel')}
            </button>
            <button className="filter-panel__apply-button" onClick={handleApplyFilters}>
              {t('exploreListings.showResults')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FilterPanel;
